import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Bot,
  Play,
  Pause,
  Settings2,
  Zap,
  Clock,
  Target,
  TrendingUp,
  TrendingDown,
  Shield,
  Check,
  Loader2,
  RefreshCw,
  Calendar,
  Timer,
  DollarSign,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  X,
  FlaskConical,
  Wallet,
  Globe,
  ChevronDown,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { apiClient, type TradingParameters, type BotConfigResponse, type ESPNGame, type SportCategory, type LeagueInfo } from '@/api/client';
import { logger } from '@/lib/logger';

// Game type for frontend display
interface GameData {
  id: string;
  homeTeam: string;
  awayTeam: string;
  startTime: string;
  status: 'upcoming' | 'live' | 'final';
  currentPeriod?: string;
  clock?: string;
  homeOdds: number;
  awayOdds: number;
  volume: number;
}

// Selected game with side preference
interface SelectedGame {
  game: GameData;
  sport: string;
  side: 'home' | 'away';  // Which team to bet on
}

// Trading parameters interface (matches API TradingParameters)
interface TradingParams {
  probabilityDrop: number;      // % drop from pregame to trigger entry
  minVolume: number;            // Minimum market volume ($)
  positionSize: number;         // Max investment per market ($)
  takeProfit: number;           // Take profit %
  stopLoss: number;             // Stop loss %
  latestEntryTime: number;      // Minutes remaining - no buys after this
  latestExitTime: number;       // Minutes remaining - must sell by this
}

// Default parameters
const DEFAULT_PARAMS: TradingParams = {
  probabilityDrop: 15,
  minVolume: 50000,
  positionSize: 100,
  takeProfit: 25,
  stopLoss: 15,
  latestEntryTime: 10,
  latestExitTime: 2,
};

// Convert local params to API format
const toApiParams = (params: TradingParams): TradingParameters => ({
  probability_drop: params.probabilityDrop,
  min_volume: params.minVolume,
  position_size: params.positionSize,
  take_profit: params.takeProfit,
  stop_loss: params.stopLoss,
  latest_entry_time: params.latestEntryTime,
  latest_exit_time: params.latestExitTime,
});

// Convert API params to local format
const fromApiParams = (params: TradingParameters): TradingParams => ({
  probabilityDrop: params.probability_drop,
  minVolume: params.min_volume,
  positionSize: params.position_size,
  takeProfit: params.take_profit,
  stopLoss: params.stop_loss,
  latestEntryTime: params.latest_entry_time,
  latestExitTime: params.latest_exit_time,
});

export default function BotConfig() {
  const location = useLocation();

  const [botEnabled, setBotEnabled] = useState(false);
  const [selectedSport, setSelectedSport] = useState<string>('nba');
  // Changed: Now tracks game ID -> SelectedGame with side preference
  // Using Record instead of Map to avoid React serialization issues
  const [selectedGames, setSelectedGames] = useState<Record<string, SelectedGame>>({});
  const [tradingParams, setTradingParams] = useState<TradingParams>(DEFAULT_PARAMS);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Always use live trading mode (no paper trading - Kalshi has no demo)
  const simulationMode = false;

  // Wallet/credentials connected status
  const [walletConnected, setWalletConnected] = useState<boolean | null>(null);

  // League selection state (new)
  const [categories, setCategories] = useState<SportCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('basketball');
  const [selectedLeague, setSelectedLeague] = useState<string>('nba');
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Check if games were passed from Markets page
  useEffect(() => {
    const state = location.state as { selectedGames?: any[]; fromMarkets?: boolean } | null;
    if (state?.fromMarkets && state.selectedGames && state.selectedGames.length > 0) {
      // Convert passed games to selectedGames record format
      const gamesRecord: Record<string, SelectedGame> = {};
      state.selectedGames.forEach((game: any) => {
        gamesRecord[game.id] = {
          game: {
            id: game.id,
            homeTeam: game.homeTeam,
            awayTeam: game.awayTeam,
            startTime: game.startTime,
            status: game.status || 'upcoming',
            homeOdds: game.homeOdds || 50,
            awayOdds: game.awayOdds || 50,
            volume: game.volume || 0,
          },
          sport: game.sport || 'nba',
          side: 'home', // Default to home team, user can change
        };
      });
      setSelectedGames(gamesRecord);

      // Set the league from first game
      if (state.selectedGames[0]?.sport) {
        setSelectedLeague(state.selectedGames[0].sport);
        setSelectedSport(state.selectedGames[0].sport);
      }

      setSuccessMessage(`${state.selectedGames.length} game(s) imported from Markets. Select which team to bet on for each game.`);
      setTimeout(() => setSuccessMessage(null), 5000);

      // Clear the navigation state to prevent re-importing on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        const data = await apiClient.getSportCategories();
        setCategories(data);
      } catch (err) {
        logger.error('Failed to load categories:', err);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  // Get leagues for current category
  const getCurrentLeagues = useCallback((): LeagueInfo[] => {
    const category = categories.find(c => c.category === selectedCategory);
    return category?.leagues || [];
  }, [categories, selectedCategory]);

  // Check wallet status on mount
  useEffect(() => {
    const checkWalletStatus = async () => {
      try {
        const status = await apiClient.getOnboardingStatus();
        setWalletConnected(status.wallet_connected);
      } catch (err) {
        setWalletConnected(false);
      }
    };
    checkWalletStatus();
  }, []);

  // Load existing bot config on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config: BotConfigResponse = await apiClient.getBotConfig();
        setBotEnabled(config.is_running);
        if (config.sport) {
          setSelectedSport(config.sport);
          // Find the correct category for the sport
          const cat = categories.find(c => c.leagues.some(l => l.league_key === config.sport));
          if (cat) {
            setSelectedCategory(cat.category);
            setSelectedLeague(config.sport);
          }
        }
        // Note: selectedGames is a Map<string, SelectedGame> - we can't restore it fully 
        // without the game data, so we'll let the user re-select from the available games
        if (config.parameters) setTradingParams(fromApiParams(config.parameters));
        // Simulation mode removed - always live trading for Kalshi
      } catch (err) {
        // Using defaults when no config exists
      }
    };
    loadConfig();
  }, [categories]);

  // State for real games from ESPN
  const [availableGames, setAvailableGames] = useState<GameData[]>([]);
  const [isLoadingGames, setIsLoadingGames] = useState(false);

  // Fetch games from ESPN API when league changes
  const fetchGames = useCallback(async (league: string) => {
    setIsLoadingGames(true);
    setError(null);
    try {
      const games = await apiClient.getLiveGames(league);
      // Transform ESPN games to our GameData format
      const transformed: GameData[] = games.map((g) => ({
        id: g.id,
        homeTeam: g.homeTeam,
        awayTeam: g.awayTeam,
        startTime: g.startTime
          ? new Date(g.startTime).toLocaleString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short'
          })
          : 'TBD',
        status: g.status,
        currentPeriod: g.currentPeriod,
        clock: g.clock,
        homeOdds: g.homeOdds,
        awayOdds: g.awayOdds,
        volume: g.volume,
      }));
      setAvailableGames(transformed);
    } catch (err) {
      logger.error('Failed to fetch games:', err);
      setAvailableGames([]);
      // Don't show error to user - just show empty list
    } finally {
      setIsLoadingGames(false);
    }
  }, []);

  // Fetch games when league changes
  useEffect(() => {
    fetchGames(selectedLeague);
  }, [selectedLeague, fetchGames]);

  // Get current league info
  const currentLeague = getCurrentLeagues().find(l => l.league_key === selectedLeague);

  // Determine if current sport has a game clock (for time-based rules)
  const hasGameClock = (): boolean => {
    const sportType = currentLeague?.sport_type?.toLowerCase() || selectedLeague.toLowerCase();
    // Sports without game clocks: baseball, golf, tennis
    const noClockSports = ['mlb', 'baseball', 'golf', 'tennis'];
    return !noClockSports.some(s => sportType.includes(s));
  };

  // Get sport-specific time configuration for sliders
  const getSportTimeConfig = (): {
    entryLabel: string;
    exitLabel: string;
    entryMax: number;
    exitMax: number;
    entryHelper: string;
    exitHelper: string;
  } => {
    const sport = selectedLeague.toLowerCase();

    if (sport.includes('nfl') || sport.includes('football')) {
      return {
        entryLabel: 'Min Time Remaining (Quarter)',
        exitLabel: 'Exit Before (Game End)',
        entryMax: 45, // Up to 45 min remaining in game (3 quarters)
        exitMax: 15,
        entryHelper: 'Minimum minutes left in quarter to enter trade',
        exitHelper: 'Must close positions with this much time left in game',
      };
    } else if (sport.includes('nba') || sport.includes('basketball')) {
      return {
        entryLabel: 'Min Time Remaining (Quarter)',
        exitLabel: 'Exit Before (Game End)',
        entryMax: 36, // Up to 36 min remaining (3 quarters)
        exitMax: 12,
        entryHelper: 'Minimum minutes left in quarter to enter trade',
        exitHelper: 'Must close positions with this much time left in game',
      };
    } else if (sport.includes('nhl') || sport.includes('hockey')) {
      return {
        entryLabel: 'Min Time Remaining (Period)',
        exitLabel: 'Exit Before (Game End)',
        entryMax: 40, // Up to 40 min remaining (2 periods)
        exitMax: 10,
        entryHelper: 'Minimum minutes left in period to enter trade',
        exitHelper: 'Must close positions with this much time left in game',
      };
    } else if (sport.includes('soccer') || sport.includes('mls') || sport.includes('epl') || sport.includes('laliga')) {
      return {
        entryLabel: 'Max Elapsed Minutes',
        exitLabel: 'Exit Before (90th Min)',
        entryMax: 75, // Can enter up to 75th minute
        exitMax: 20,
        entryHelper: 'Latest game minute to enter trade (max 75)',
        exitHelper: 'Minutes before 90th minute to close positions',
      };
    } else {
      return {
        entryLabel: 'Latest Entry Time',
        exitLabel: 'Latest Exit Time',
        entryMax: 30,
        exitMax: 15,
        entryHelper: 'No new positions after this much time remaining',
        exitHelper: 'Must close all positions by this time remaining',
      };
    }
  };

  const sportTimeConfig = getSportTimeConfig();

  // Get selected games data (from current sport only for display)
  const selectedGamesData = Object.values(selectedGames);
  const selectedGamesCount = Object.keys(selectedGames).length;

  // Toggle game selection with default side = home
  const toggleGameSelection = (gameId: string, game: GameData) => {
    setSelectedGames(prev => {
      const newObj = { ...prev };
      if (gameId in newObj) {
        delete newObj[gameId];
      } else {
        // Default to home team when first selecting
        newObj[gameId] = {
          game,
          sport: selectedLeague,
          side: 'home'
        };
      }
      return newObj;
    });
  };

  // Change which team is selected for a game
  const changeSideSelection = (gameId: string, side: 'home' | 'away') => {
    setSelectedGames(prev => {
      const existing = prev[gameId];
      if (existing) {
        return { ...prev, [gameId]: { ...existing, side } };
      }
      return prev;
    });
  };

  // Select all games (default to home team)
  const selectAllGames = () => {
    const newObj: Record<string, SelectedGame> = {};
    availableGames.forEach(g => {
      newObj[g.id] = { game: g, sport: selectedLeague, side: 'home' };
    });
    setSelectedGames(newObj);
  };

  // Clear all selections
  const clearAllGames = () => {
    setSelectedGames({});
  };

  // Handle category change
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    // Set first league in category as default
    const cat = categories.find(c => c.category === category);
    if (cat && cat.leagues.length > 0) {
      setSelectedLeague(cat.leagues[0].league_key);
    }
    setError(null);
    setSuccessMessage(null);
  };

  // Handle league change
  const handleLeagueChange = (league: string) => {
    setSelectedLeague(league);
    setSelectedSport(league); // Keep backward compatibility
    setError(null);
    setSuccessMessage(null);
  };

  // Handle parameter change
  const updateParam = (key: keyof TradingParams, value: number) => {
    setTradingParams(prev => ({ ...prev, [key]: value }));
  };

  // Save configuration to API
  const handleSave = useCallback(async () => {
    if (selectedGamesCount === 0) {
      setError('Please select at least one game');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Get all selected games with their side preferences
      const allSelectedGames = Object.values(selectedGames);
      const firstSelection = allSelectedGames[0];

      if (firstSelection) {
        // Build additional games list for multi-sport support
        const additionalGames = allSelectedGames.slice(1).map(sel => ({
          game_id: sel.game.id,
          sport: sel.sport,
          home_team: sel.game.homeTeam,
          away_team: sel.game.awayTeam,
          start_time: sel.game.startTime,
          selected_side: sel.side,  // Include which team to bet on
        }));

        await apiClient.saveBotConfig({
          sport: firstSelection.sport,
          game: {
            game_id: firstSelection.game.id,
            sport: firstSelection.sport,
            home_team: firstSelection.game.homeTeam,
            away_team: firstSelection.game.awayTeam,
            start_time: firstSelection.game.startTime,
            selected_side: firstSelection.side,  // Include which team to bet on
          },
          additional_games: additionalGames.length > 0 ? additionalGames : undefined,
          parameters: toApiParams(tradingParams),
          simulation_mode: simulationMode,
        });
      }
      const modeStr = simulationMode ? 'Paper Trading' : 'Live Trading';
      const teamsStr = allSelectedGames.map(s =>
        s.side === 'home' ? s.game.homeTeam : s.game.awayTeam
      ).join(', ');
      setSuccessMessage(`Configuration saved (${modeStr}) betting on: ${teamsStr}`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  }, [selectedGames, tradingParams, simulationMode]);

  // Toggle bot on/off
  const handleToggleBot = useCallback(async () => {
    if (selectedGamesCount === 0) {
      setError('Please select at least one game');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // First save the config with simulation mode and selected sides
      const allSelectedGames = Object.values(selectedGames);
      const firstSelection = allSelectedGames[0];

      if (firstSelection) {
        const additionalGames = allSelectedGames.slice(1).map(sel => ({
          game_id: sel.game.id,
          sport: sel.sport,
          home_team: sel.game.homeTeam,
          away_team: sel.game.awayTeam,
          start_time: sel.game.startTime,
          selected_side: sel.side,
        }));

        await apiClient.saveBotConfig({
          sport: firstSelection.sport,
          game: {
            game_id: firstSelection.game.id,
            sport: firstSelection.sport,
            home_team: firstSelection.game.homeTeam,
            away_team: firstSelection.game.awayTeam,
            start_time: firstSelection.game.startTime,
            selected_side: firstSelection.side,
          },
          additional_games: additionalGames.length > 0 ? additionalGames : undefined,
          parameters: toApiParams(tradingParams),
          simulation_mode: simulationMode,
        });
      }

      // Then start/stop the bot
      if (botEnabled) {
        await apiClient.stopBot();
        setBotEnabled(false);
        setSuccessMessage('Bot stopped');
      } else {
        await apiClient.startBot();
        setBotEnabled(true);
        const modeStr = simulationMode ? '(Paper Trading)' : '(Live Trading)';
        const teamsStr = allSelectedGames.map(s =>
          s.side === 'home' ? s.game.homeTeam : s.game.awayTeam
        ).join(', ');
        setSuccessMessage(`Bot started ${modeStr} betting on: ${teamsStr}`);
      }
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle bot');
    } finally {
      setIsLoading(false);
    }
  }, [botEnabled, selectedGames, tradingParams, simulationMode]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
              <Bot className="w-6 h-6" />
              Bot Configuration
            </h1>
            <p className="text-muted-foreground mt-1">
              Select games and configure trading parameters
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Live Trading Mode Indicator */}
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-green-500/10 border-green-500/30 text-green-400"
            >
              <Wallet className="w-4 h-4" />
              <span className="text-sm font-medium">Live Trading</span>
            </div>
            <Badge
              variant={botEnabled ? 'default' : 'secondary'}
              className={cn(
                'px-3 py-1',
                botEnabled && 'bg-green-500/20 text-green-400 border border-green-500/30'
              )}
            >
              {botEnabled ? `Bot Running (${selectedGamesCount} game${selectedGamesCount !== 1 ? 's' : ''})` : 'Bot Stopped'}
            </Badge>
            <Button
              onClick={handleToggleBot}
              variant={botEnabled ? 'destructive' : 'default'}
              disabled={isLoading || selectedGamesCount === 0 || walletConnected === false}
              className="gap-2"
              data-tour="start-bot"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : botEnabled ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {botEnabled ? 'Stop Bot' : 'Start Bot'}
            </Button>
          </div>
        </div>

        {/* Wallet Not Connected Warning */}
        {walletConnected === false && (
          <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5" />
              <div>
                <h3 className="font-semibold text-orange-400">Credentials Not Connected</h3>
                <p className="text-sm text-orange-400/80 mt-1">
                  Please connect your Kalshi API credentials or Polymarket wallet in Settings before starting the bot.
                </p>
                <a
                  href="/settings"
                  className="mt-2 text-xs text-orange-400 underline hover:no-underline inline-block"
                >
                  Go to Settings
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Live Trading Notice */}

        {/* Error/Success Messages */}
        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}
        {successMessage && (
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 flex items-center gap-2">
            <Check className="w-4 h-4" />
            {successMessage}
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Game Selection */}
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Game Selection
                </CardTitle>
                <CardDescription>
                  Select multiple games to trade on simultaneously
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Category Selector */}
                <div className="space-y-2" data-tour="category-selector">
                  <Label className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    Category
                  </Label>
                  <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingCategories ? (
                        <SelectItem value="loading" disabled>Loading...</SelectItem>
                      ) : (
                        categories.map(cat => (
                          <SelectItem key={cat.category} value={cat.category}>
                            {cat.display_name} ({cat.leagues.length} leagues)
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* League Selector */}
                <div className="space-y-2" data-tour="league-selector">
                  <Label>League</Label>
                  <Select value={selectedLeague} onValueChange={handleLeagueChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select league" />
                    </SelectTrigger>
                    <SelectContent>
                      {getCurrentLeagues().map(league => (
                        <SelectItem key={league.league_key} value={league.league_key}>
                          {league.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {currentLeague && (
                    <p className="text-xs text-muted-foreground">
                      Sport type: {currentLeague.sport_type}
                    </p>
                  )}
                </div>

                {/* Games List Header */}
                <div className="flex items-center justify-between pt-2">
                  <Label>
                    Games {isLoadingGames && <span className="text-xs text-muted-foreground">(loading...)</span>}
                  </Label>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={selectAllGames}
                      disabled={availableGames.length === 0}
                      className="text-xs h-7"
                    >
                      Select All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAllGames}
                      disabled={selectedGamesCount === 0}
                      className="text-xs h-7"
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                {/* Games List - Multi-select with team selection */}
                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1" data-tour="games-list">
                  {isLoadingGames ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                      <span className="ml-2 text-muted-foreground">Loading games from ESPN...</span>
                    </div>
                  ) : availableGames.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No games scheduled</p>
                      <p className="text-xs mt-1">Try selecting a different sport</p>
                    </div>
                  ) : (
                    availableGames.map(game => {
                      const selection = selectedGames[game.id];
                      const isSelected = !!selection;

                      return (
                        <div
                          key={game.id}
                          className={cn(
                            'p-3 rounded-lg border transition-all',
                            isSelected
                              ? 'bg-primary/10 border-primary/50'
                              : 'bg-muted/30 border-border hover:border-primary/30'
                          )}
                        >
                          <div className="flex items-start justify-between">
                            <div
                              className="flex-1 cursor-pointer"
                              onClick={() => toggleGameSelection(game.id, game)}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Badge
                                  variant={game.status === 'live' ? 'default' : 'secondary'}
                                  className={cn(
                                    'text-xs',
                                    game.status === 'live' && 'bg-red-500/20 text-red-400 animate-pulse'
                                  )}
                                >
                                  {game.status === 'live' ? 'LIVE' : game.status === 'final' ? 'Final' : 'Upcoming'}
                                </Badge>
                                {game.status === 'live' && game.currentPeriod && (
                                  <span className="text-xs text-muted-foreground">
                                    {game.currentPeriod} {game.clock}
                                  </span>
                                )}
                              </div>
                              <p className="font-medium text-sm">
                                {game.awayTeam} @ {game.homeTeam}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {game.startTime}
                              </p>
                            </div>
                            <div
                              className={cn(
                                'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors mt-1 cursor-pointer',
                                isSelected
                                  ? 'bg-primary border-primary'
                                  : 'border-muted-foreground/30'
                              )}
                              onClick={() => toggleGameSelection(game.id, game)}
                            >
                              {isSelected && (
                                <Check className="w-3 h-3 text-primary-foreground" />
                              )}
                            </div>
                          </div>

                          {/* Team Selection - shown when game is selected */}
                          {isSelected && (
                            <div className="mt-3 pt-3 border-t border-primary/20">
                              <p className="text-xs text-muted-foreground mb-2">Bet on which team?</p>
                              <div className="flex gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    changeSideSelection(game.id, 'away');
                                  }}
                                  className={cn(
                                    'flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all border',
                                    selection?.side === 'away'
                                      ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                                      : 'bg-muted/50 border-border text-muted-foreground hover:border-blue-500/30'
                                  )}
                                >
                                  {game.awayTeam}
                                  {selection?.side === 'away' && (
                                    <Check className="w-3 h-3 inline ml-1" />
                                  )}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    changeSideSelection(game.id, 'home');
                                  }}
                                  className={cn(
                                    'flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all border',
                                    selection?.side === 'home'
                                      ? 'bg-green-500/20 border-green-500/50 text-green-400'
                                      : 'bg-muted/50 border-border text-muted-foreground hover:border-green-500/30'
                                  )}
                                >
                                  {game.homeTeam}
                                  {selection?.side === 'home' && (
                                    <Check className="w-3 h-3 inline ml-1" />
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Selection Summary */}
                {selectedGamesCount > 0 && (
                  <div className="p-3 rounded-lg bg-primary/10 border border-primary/30">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">
                        {selectedGamesCount} game{selectedGamesCount > 1 ? 's' : ''} selected
                      </span>
                    </div>
                    <div className="mt-2 space-y-1">
                      {Object.values(selectedGames).map(sel => (
                        <div key={sel.game.id} className="text-xs flex items-center gap-1">
                          <span className="text-muted-foreground">{sel.sport.toUpperCase()}:</span>
                          <span className={cn(
                            'font-medium',
                            sel.side === 'home' ? 'text-green-400' : 'text-blue-400'
                          )}>
                            {sel.side === 'home' ? sel.game.homeTeam : sel.game.awayTeam}
                          </span>
                          <span className="text-muted-foreground">
                            ({sel.side === 'home' ? 'home' : 'away'})
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Refresh Button */}
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={() => fetchGames(selectedSport)}
                  disabled={isLoadingGames}
                >
                  {isLoadingGames ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  {isLoadingGames ? 'Loading...' : 'Refresh Games'}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Trading Parameters */}
          <div className="lg:col-span-2" data-tour="trading-params">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings2 className="w-5 h-5" />
                  Trading Parameters
                </CardTitle>
                <CardDescription>
                  Configure entry/exit conditions and position sizing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Entry Conditions */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Target className="w-4 h-4 text-primary" />
                    Entry Conditions
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Probability Drop */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <TrendingDown className="w-4 h-4 text-red-400" />
                          Probability Drop
                        </Label>
                        <span className="font-mono text-sm text-primary">
                          {tradingParams.probabilityDrop}%
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Minimum drop from pregame odds to trigger entry
                      </p>
                      <Slider
                        value={[tradingParams.probabilityDrop]}
                        onValueChange={([v]) => updateParam('probabilityDrop', v)}
                        min={5}
                        max={50}
                        step={1}
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>5%</span>
                        <span>50%</span>
                      </div>
                    </div>

                    {/* Minimum Volume */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-blue-400" />
                          Minimum Volume
                        </Label>
                        <span className="font-mono text-sm text-primary">
                          ${tradingParams.minVolume.toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Minimum market volume to enter a position
                      </p>
                      <Slider
                        value={[tradingParams.minVolume]}
                        onValueChange={([v]) => updateParam('minVolume', v)}
                        min={10000}
                        max={500000}
                        step={10000}
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>$10K</span>
                        <span>$500K</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Position Sizing */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-primary" />
                    Position Sizing
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-yellow-400" />
                        Position Size
                      </Label>
                      <span className="font-mono text-sm text-primary">
                        ${tradingParams.positionSize}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Maximum amount to invest in this market
                    </p>
                    <Slider
                      value={[tradingParams.positionSize]}
                      onValueChange={([v]) => updateParam('positionSize', v)}
                      min={10}
                      max={1000}
                      step={10}
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>$10</span>
                      <span>$1,000</span>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Exit Conditions */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    Exit Conditions
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Take Profit */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-green-400" />
                          Take Profit
                        </Label>
                        <span className="font-mono text-sm text-green-400">
                          +{tradingParams.takeProfit}%
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Automatically sell when profit reaches this level
                      </p>
                      <Slider
                        value={[tradingParams.takeProfit]}
                        onValueChange={([v]) => updateParam('takeProfit', v)}
                        min={5}
                        max={100}
                        step={5}
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>5%</span>
                        <span>100%</span>
                      </div>
                    </div>

                    {/* Stop Loss */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-400" />
                          Stop Loss
                        </Label>
                        <span className="font-mono text-sm text-red-400">
                          -{tradingParams.stopLoss}%
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Automatically sell to limit losses
                      </p>
                      <Slider
                        value={[tradingParams.stopLoss]}
                        onValueChange={([v]) => updateParam('stopLoss', v)}
                        min={5}
                        max={50}
                        step={5}
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>5%</span>
                        <span>50%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Time-Based Rules */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Timer className="w-4 h-4 text-primary" />
                    Time-Based Rules
                    {!hasGameClock() && (
                      <Badge variant="secondary" className="text-xs">
                        N/A for {currentLeague?.display_name || selectedLeague}
                      </Badge>
                    )}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Latest Entry Time */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-orange-400" />
                          {sportTimeConfig.entryLabel}
                        </Label>
                        <span className="font-mono text-sm text-primary">
                          {tradingParams.latestEntryTime} min
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {sportTimeConfig.entryHelper}
                      </p>
                      <Slider
                        value={[tradingParams.latestEntryTime]}
                        onValueChange={([v]) => updateParam('latestEntryTime', v)}
                        min={1}
                        max={sportTimeConfig.entryMax}
                        step={1}
                        disabled={!hasGameClock()}
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>1 min</span>
                        <span>{sportTimeConfig.entryMax} min</span>
                      </div>
                    </div>

                    {/* Latest Exit Time */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-purple-400" />
                          {sportTimeConfig.exitLabel}
                        </Label>
                        <span className="font-mono text-sm text-primary">
                          {tradingParams.latestExitTime} min
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {sportTimeConfig.exitHelper}
                      </p>
                      <Slider
                        value={[tradingParams.latestExitTime]}
                        onValueChange={([v]) => updateParam('latestExitTime', v)}
                        min={0}
                        max={sportTimeConfig.exitMax}
                        step={1}
                        disabled={!hasGameClock()}
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>0 min</span>
                        <span>{sportTimeConfig.exitMax} min</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Save Button */}
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setTradingParams(DEFAULT_PARAMS)}
                  >
                    Reset to Defaults
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="gap-2"
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    Save Configuration
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quick Stats Footer */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Selected Games</div>
            <div className="text-lg font-semibold">
              {selectedGamesCount > 0
                ? `${selectedGamesCount} game${selectedGamesCount !== 1 ? 's' : ''}`
                : 'None'
              }
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Max Exposure</div>
            <div className="text-lg font-semibold">${tradingParams.positionSize * Math.max(1, selectedGamesCount)}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Risk/Reward</div>
            <div className="text-lg font-semibold">
              <span className="text-red-400">-{tradingParams.stopLoss}%</span>
              {' / '}
              <span className="text-green-400">+{tradingParams.takeProfit}%</span>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Bot Status</div>
            <div className={cn(
              'text-lg font-semibold',
              botEnabled ? 'text-green-400' : 'text-muted-foreground'
            )}>
              {botEnabled ? 'Active' : 'Inactive'}
            </div>
          </Card>
        </div>

        {/* Selected Games Summary - shown below stats when games are selected */}
        {selectedGamesCount > 0 && (
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-primary" />
                <span className="font-medium">Active Betting Selection</span>
              </div>
              <Badge variant="outline">{selectedGamesCount} team{selectedGamesCount !== 1 ? 's' : ''}</Badge>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedGamesData.map(sel => (
                <Badge
                  key={sel.game.id}
                  variant="secondary"
                  className={cn(
                    'flex items-center gap-1 pr-1',
                    sel.side === 'home' ? 'bg-green-500/10 border-green-500/30' : 'bg-blue-500/10 border-blue-500/30'
                  )}
                >
                  <span className="text-muted-foreground text-xs mr-1">{sel.sport.toUpperCase()}</span>
                  <span className={cn(
                    'font-medium',
                    sel.game.status === 'live' && 'text-red-400'
                  )}>
                    {sel.side === 'home' ? sel.game.homeTeam : sel.game.awayTeam}
                  </span>
                  <span className="text-muted-foreground text-xs ml-1">
                    vs {sel.side === 'home' ? sel.game.awayTeam : sel.game.homeTeam}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGameSelection(sel.game.id, sel.game);
                    }}
                    className="ml-1 hover:bg-muted rounded p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
